import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Topbar from '@/components/layout/Topbar';
import { api } from '@/lib/api';
import {
  ArrowLeft,
  Newspaper,
  Users,
  Route,
  Gavel,
  ScrollText,
  UserCheck,
  Activity,
  Scale,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Info,
  Cpu,
  GitCompare,
} from 'lucide-react';

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : Number(n).toFixed(d));
const pct = (n, d = 2) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : (Number(n) * 100).toFixed(d) + '%');
const iso = (s) => (s ? new Date(s).toLocaleString('es-AR', { hour12: false }) : '—');

const SECTIONS = [
  { id: 'diversidad', label: 'Diversidad de modelos', icon: Cpu },
  { id: 'origen', label: 'Snapshot origen', icon: Newspaper },
  { id: 'analistas', label: 'Analistas A1–A5', icon: Users },
  { id: 'candidata', label: 'Candidata + sizing', icon: Route },
  { id: 'tribunal', label: 'Tribunal F1–F3', icon: Gavel },
  { id: 'comite', label: 'Comité', icon: ScrollText },
  { id: 'jorge', label: 'Decisión Jorge', icon: UserCheck },
  { id: 'ejecucion', label: 'Ejecución + cierre', icon: Activity },
  { id: 'sombra', label: 'Simulación en sombra', icon: GitCompare },
  { id: 'juicio', label: 'Mi juicio', icon: Scale },
];

const ANALISTA_META = {
  A1_noticias: { label: 'A1 · Noticias', hint: 'Flujo de titulares' },
  A2_macro: { label: 'A2 · Macro', hint: 'Contexto tasas / dólar / riesgo' },
  A3_tecnico: { label: 'A3 · Técnico', hint: 'Estructura, medias, ATR' },
  A4_onchain: { label: 'A4 · On-chain / Flujos', hint: 'Pendiente de fuente' },
  A5_regimen: { label: 'A5 · Régimen del día', hint: 'Interruptor de estrategias' },
};

const STATE_LABEL = {
  esperando_jorge: 'Esperando decisión de Jorge',
  rechazada_tribunal: 'Rechazada por tribunal',
  rechazada_comite: 'Rechazada por comité',
  rechazada_riesgo: 'Rechazada por motor de riesgo',
  rechazada_jorge: 'Rechazada por Jorge',
  aprobada_jorge: 'Aprobada por Jorge',
  en_ejecucion: 'En ejecución',
  cerrada: 'Cerrada',
  shadow: 'Shadow sim (S5-B)',
  expirada: 'Expirada',
  bloqueada_regimen: 'Bloqueada por régimen (A5)',
  propuesta: 'Propuesta',
};

const ESTADOS_VETADAS_O_RECHAZADAS = [
  'rechazada_tribunal', 'rechazada_comite', 'rechazada_riesgo', 'rechazada_jorge', 'shadow', 'expirada', 'bloqueada_regimen',
];

function SectionHeader({ id, icon: Icon, title, subtitle }) {
  return (
    <div id={id} className="scroll-mt-24 flex items-center gap-2 mb-3">
      <Icon size={16} className="text-[hsl(var(--brand-accent))]" />
      <h2 className="font-display text-lg">{title}</h2>
      {subtitle && <span className="text-xs text-muted-foreground">· {subtitle}</span>}
    </div>
  );
}

function KV({ k, v, mono = false, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-border/60 last:border-b-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className={mono ? 'font-num text-sm' : 'text-sm'} data-testid={testId}>{v}</span>
    </div>
  );
}

function PairBlock({ pair }) {
  return (
    <div className="panel-soft p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm">{pair.symbol}</div>
        <div className={`text-xs font-num ${pair.change_pct_day > 0 ? 'pnl-pos' : pair.change_pct_day < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>
          {(pair.change_pct_day * 100).toFixed(2)}%
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 text-xs">
        <KV k="Precio" v={<span className="font-num">{fmt(pair.price, 2)}</span>} />
        <KV k="Apertura" v={<span className="font-num">{fmt(pair.open_day, 2)}</span>} />
        <KV k="ATR 1h" v={<span className="font-num">{fmt(pair.atr_1h, 2)}</span>} />
        <KV k="Vol. rel." v={<span className="font-num">{fmt(pair.volumen_relativo, 2)}</span>} />
        <KV k="EMA 20" v={<span className="font-num">{fmt(pair.ema_20_1h, 2)}</span>} />
        <KV k="EMA 50" v={<span className="font-num">{fmt(pair.ema_50_1h, 2)}</span>} />
        <KV k="EMA 200 4h" v={<span className="font-num">{fmt(pair.ema_200_4h, 2)}</span>} />
        <KV k="Sop / Res" v={<span className="font-num">{fmt(pair.soporte_cercano, 0)} / {fmt(pair.resistencia_cercana, 0)}</span>} />
      </div>
    </div>
  );
}

function AnalystCard({ lectura }) {
  const meta = ANALISTA_META[lectura.analista] || { label: lectura.analista, hint: '' };
  const isA4pending = lectura.analista === 'A4_onchain' && lectura.pending_source;
  const isA5 = lectura.analista === 'A5_regimen';
  const biasClass =
    lectura.sesgo === 'alcista' ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10'
      : lectura.sesgo === 'bajista' ? 'text-rose-400 border-rose-500/40 bg-rose-500/10'
        : 'text-slate-300 border-slate-500/30 bg-slate-500/10';

  return (
    <article className="panel p-3" data-testid={`analyst-${lectura.analista}`}>
      <header className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-display text-sm font-semibold">{meta.label}</div>
          <div className="text-[11px] text-muted-foreground">
            {isA4pending
              ? 'pendiente de fuente (silencio activo)'
              : `${lectura.proveedor_llm || 'n/a'} · ${lectura.modelo_llm || 'n/a'}`}
          </div>
        </div>
        {isA4pending ? (
          <span className="alarm-warning text-[10.5px] px-2 py-0.5 rounded border">SIN FUENTE</span>
        ) : (
          <span className={`text-[10.5px] px-2 py-0.5 rounded border ${biasClass}`}>
            {lectura.sesgo?.toUpperCase() || 'NEUTRO'} · conv <span className="font-num">{fmt(lectura.convicción ?? lectura.conviccion, 2)}</span>
          </span>
        )}
      </header>
      {isA5 && (
        <div className="panel-soft px-3 py-2 mb-2 text-xs" data-testid="a5-regime-summary">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div><span className="text-muted-foreground">Régimen: </span><span className="font-medium">{lectura.regimen || '—'}</span></div>
            <div><span className="text-muted-foreground">Volatilidad: </span><span className="font-medium">{lectura.volatilidad || '—'}</span></div>
            <div>
              <span className="text-muted-foreground">Estrategias habilitadas: </span>
              <span className="font-medium">{(lectura.estrategias_habilitadas || []).join(', ') || 'ninguna'}</span>
            </div>
          </div>
        </div>
      )}
      {lectura.resumen && <p className="text-sm whitespace-pre-line mb-2">{lectura.resumen}</p>}
      {(lectura.puntos_clave?.length > 0) && (
        <div className="text-xs mb-1"><span className="text-muted-foreground">Puntos clave: </span>{lectura.puntos_clave.join('; ')}</div>
      )}
      {(lectura.riesgos?.length > 0) && (
        <div className="text-xs"><span className="text-muted-foreground">Riesgos: </span>{lectura.riesgos.join('; ')}</div>
      )}
      {(lectura.par_favorito && lectura.par_favorito !== 'ninguno') && (
        <div className="text-[11px] text-muted-foreground mt-1">Par favorito: <span className="font-num">{lectura.par_favorito}</span></div>
      )}
    </article>
  );
}

function FiscalActa({ acta }) {
  const sev = acta.severidad || 'media';
  const sevClass = sev === 'alta' ? 'text-rose-400 border-rose-500/40 bg-rose-500/10'
    : sev === 'media' ? 'text-amber-300 border-amber-500/40 bg-amber-500/10'
      : 'text-slate-300 border-slate-500/30 bg-slate-500/10';
  return (
    <article className="panel p-3" data-testid={`acta-${acta.fiscal}`}>
      <header className="flex items-center gap-2 flex-wrap mb-2">
        <span className="font-display font-semibold text-sm">{acta.fiscal}</span>
        <span className="text-[11px] text-muted-foreground">{acta.proveedor_llm} · {acta.modelo_llm}</span>
        <span className={`text-[10.5px] px-2 py-0.5 rounded border ${sevClass}`}>severidad {sev}</span>
        {acta.veto
          ? <span className="text-[10.5px] px-2 py-0.5 rounded border alarm-critical">VETO</span>
          : <span className="text-[10.5px] px-2 py-0.5 rounded border text-slate-300 border-slate-500/30 bg-slate-500/10">sin veto</span>}
        <span className="text-[11px] text-muted-foreground">
          fuerza oposición <span className="font-num">{fmt(acta.fuerza_oposicion, 2)}</span>
        </span>
      </header>
      <div className="text-xs text-muted-foreground mb-1">Objeciones íntegras:</div>
      <ul className="list-disc pl-5 space-y-1 text-sm">
        {(acta.objeciones || []).map((o, i) => <li key={i}>{o}</li>)}
      </ul>
      {acta.resumen_veredicto && (
        <p className="text-xs italic text-muted-foreground mt-2 border-t border-border/60 pt-2">“{acta.resumen_veredicto}”</p>
      )}
      {(acta.fiscal === 'F3' && acta.reglas_verificadas?.length > 0) && (
        <div className="text-[11px] text-muted-foreground mt-2">
          Reglas R verificadas: <span className="font-num">{acta.reglas_verificadas.join(', ')}</span>
        </div>
      )}
    </article>
  );
}

const PROVIDER_BADGE = {
  openai: { label: 'OpenAI', cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' },
  anthropic: { label: 'Anthropic', cls: 'text-orange-300 border-orange-500/40 bg-orange-500/10' },
  gemini: { label: 'Gemini', cls: 'text-sky-300 border-sky-500/40 bg-sky-500/10' },
  'n/a': { label: 'sin fuente', cls: 'text-slate-300 border-slate-500/30 bg-slate-500/10' },
};

function ProviderBadge({ provider, model }) {
  const key = (provider || 'n/a').toLowerCase();
  const info = PROVIDER_BADGE[key] || { label: provider || 'desconocido', cls: 'text-slate-300 border-slate-500/30 bg-slate-500/10' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded border ${info.cls}`} title={`${provider}/${model}`}>
      <Cpu size={11} />
      <span className="font-medium">{info.label}</span>
      <span className="font-num opacity-80">· {model || '—'}</span>
    </span>
  );
}

function LayerCard({ layer, agents }) {
  // agents: [{name, provider, model}]
  const providers = new Set(agents.map((a) => (a.provider || '').toLowerCase()));
  const uniform = providers.size <= 1;
  return (
    <div className="panel-soft p-3" data-testid={`diversity-layer-${layer.key}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="font-display font-semibold text-sm">{layer.title}</div>
          <div className="text-[11px] text-muted-foreground">{layer.hint}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${uniform ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' : 'text-amber-300 border-amber-500/40 bg-amber-500/10'}`}>
          {uniform ? 'proveedor único' : 'mixto'}
        </span>
      </div>
      <ul className="space-y-1">
        {agents.map((a, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground">{a.name}</span>
            <ProviderBadge provider={a.provider} model={a.model} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModelDiversityBoard({ lecturas, propuesta, tribunal, comite }) {
  const analistas = (lecturas || []).map((l) => ({
    name: (l.analista || '').replace('_', ' · '),
    provider: l.proveedor_llm,
    model: l.modelo_llm,
  }));
  const estrategas = propuesta ? [{
    name: propuesta.estrategia,
    provider: propuesta.proveedor_llm,
    model: propuesta.modelo_llm,
  }] : [];
  const fiscales = ((tribunal || {}).actas || []).map((a) => ({
    name: a.fiscal,
    provider: a.proveedor_llm,
    model: a.modelo_llm,
  }));
  const comites = comite ? [{
    name: 'Comité C1/C2',
    provider: comite.proveedor_llm,
    model: comite.modelo_llm,
  }] : [];
  // Diversidad cross-capa: los 4 sets deben tener providers distintos entre analistas ↔ estrategas ↔ tribunal
  const provA = new Set(analistas.map((a) => (a.provider || '').toLowerCase()).filter((x) => x && x !== 'n/a'));
  const provE = new Set(estrategas.map((a) => (a.provider || '').toLowerCase()));
  const provF = new Set(fiscales.map((a) => (a.provider || '').toLowerCase()));
  const provC = new Set(comites.map((a) => (a.provider || '').toLowerCase()));
  const analistasVsEstrategas = !!(provA.size && provE.size && [...provA].every((p) => !provE.has(p)));
  const estrategasVsTribunal = !!(provE.size && provF.size && [...provE].every((p) => !provF.has(p)));
  const analistasVsTribunal = !!(provA.size && provF.size && [...provA].every((p) => !provF.has(p)));
  const diversidadOk = analistasVsEstrategas && estrategasVsTribunal && analistasVsTribunal;
  return (
    <div className="panel p-4" data-testid="diversity-board">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="font-display text-base font-semibold">Diversidad de modelos por capa</div>
          <div className="text-xs text-muted-foreground">verifica de un vistazo que no toda la cadena corra sobre el mismo modelo</div>
        </div>
        <span
          className={`text-[11px] px-2.5 py-1 rounded border font-medium ${diversidadOk
            ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
            : 'text-rose-300 border-rose-500/40 bg-rose-500/10'}`}
          data-testid="diversity-status"
        >
          {diversidadOk ? 'diversidad OK · capas independientes' : 'diversidad ROTA — mismo proveedor en capas contiguas'}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <LayerCard layer={{ key: 'analistas', title: 'Analistas A1–A5', hint: 'lectura del mercado (5 en paralelo)' }} agents={analistas} />
        <LayerCard layer={{ key: 'estrategas', title: 'Estrategas', hint: 'propuesta de setup' }} agents={estrategas} />
        <LayerCard layer={{ key: 'tribunal', title: 'Tribunal F1–F3', hint: 'oposición adversarial' }} agents={fiscales} />
        <LayerCard layer={{ key: 'comite', title: 'Comité C1/C2', hint: 'síntesis + briefing' }} agents={comites} />
      </div>
      <div className="mt-3 text-[11px] text-muted-foreground border-t border-border pt-2">
        Regla: analistas, estrategas y tribunal deben venir de proveedores distintos para que la oposición del tribunal sea real y no un eco del mismo modelo.
      </div>
    </div>
  );
}

function ShadowSection({ shadow }) {
  if (!shadow) {
    return (
      <div className="panel p-4 text-sm text-muted-foreground">
        Esta candidata no tiene simulación en sombra registrada.
      </div>
    );
  }
  const resCls = shadow.resultado === 'ganadora' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
    : shadow.resultado === 'perdedora' ? 'text-rose-300 border-rose-500/40 bg-rose-500/10'
      : 'text-slate-300 border-slate-500/30 bg-slate-500/10';
  const rCls = shadow.r_multiplo > 0 ? 'pnl-pos' : shadow.r_multiplo < 0 ? 'pnl-neg' : 'pnl-neutral';
  return (
    <div className="panel p-4 space-y-3" data-testid="shadow-section">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded border ${resCls}`} data-testid="shadow-resultado">
          resultado hipotético: {shadow.resultado?.toUpperCase()}
        </span>
        <span className="text-[11px] text-muted-foreground">
          motivo no ejecución: <span className="font-num">{shadow.motivo_no_ejecucion}</span>
        </span>
        <span className="text-[11px] text-muted-foreground">
          slippage aplicado: <span className="font-num">{pct(shadow.slippage_por_lado_pct, 3)} / lado</span>
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        Simulación posterior al cierre de la candidata (no fue enviada a mercado): entrada penalizada por slippage,
        seguida del trailing y las salidas propias de la estrategia sobre velas simuladas. Sirve para calibrar
        si el tribunal (o Jorge) acertó al rechazar.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Entry fill</div><div className="font-num text-sm">{fmt(shadow.ejecucion?.entry_fill)}</div></div>
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Stop inicial</div><div className="font-num text-sm">{fmt(shadow.ejecucion?.stop_inicial)}</div></div>
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Salida</div><div className="font-num text-sm">{fmt(shadow.cierre?.precio_salida)}</div></div>
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Motivo cierre</div><div className="text-sm">{shadow.cierre?.motivo}</div></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">R múltiplo</div><div className={`font-num text-sm ${rCls}`}>{fmt(shadow.r_multiplo, 2)}</div></div>
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">PnL hipotético</div><div className={`font-num text-sm ${shadow.pnl_usdt > 0 ? 'pnl-pos' : shadow.pnl_usdt < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(shadow.pnl_usdt, 2)} USDT</div></div>
        <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Eventos trailing</div><div className="font-num text-sm">{(shadow.eventos || []).length}</div></div>
      </div>
      {(shadow.eventos || []).length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-1">Trailing hipotético</div>
          <ul className="text-xs space-y-1">
            {shadow.eventos.map((ev, i) => (
              <li key={i} className="panel-soft px-2 py-1 flex items-center justify-between gap-2">
                <span className="font-num text-[11px]">{iso(ev.ts)}</span>
                <span className="flex-1 px-2">{ev.tipo} · {ev.detalle}</span>
                <span className="font-num">{ev.stop_nuevo ? fmt(ev.stop_nuevo) : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Auditoria() {
  const { candidataId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [judgment, setJudgment] = useState({ habria_operado: true, nota: '' });
  const [busy, setBusy] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState('origen');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.get(`/audit/candidate/${candidataId}`);
      setData(d);
      if (d?.juicio_jorge_auditoria) {
        setJudgment({
          habria_operado: !!d.juicio_jorge_auditoria.habria_operado,
          nota: d.juicio_jorge_auditoria.nota || '',
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la auditoría');
    } finally {
      setLoading(false);
    }
  }, [candidataId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActiveAnchor(e.target.id); });
    }, { rootMargin: '-40% 0px -55% 0px', threshold: 0 });
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [data]);

  const submitJudgment = async () => {
    setBusy(true);
    try {
      await api.post(`/audit/candidate/${candidataId}/judgment`, judgment);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el juicio');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return (
    <>
      <Topbar title="Auditoría de candidata" subtitle="cargando trazabilidad completa…" />
      <div className="px-4 py-6 text-sm text-muted-foreground">Cargando…</div>
    </>
  );
  if (error || !data) return (
    <>
      <Topbar title="Auditoría de candidata" subtitle="" />
      <div className="px-4 py-6">
        <div className="panel alarm-critical p-4 text-sm" data-testid="audit-error">{error || 'Sin datos'}</div>
        <Link to="/logs" className="text-xs underline text-muted-foreground">← volver al panel de logs</Link>
      </div>
    </>
  );

  const p = data.propuesta || {};
  const sd = data.sizing_desglose || null;
  const t = data.tribunal || null;
  const c = data.comite || null;
  const dj = data.decision_jorge || null;
  const ejec = data.ejecucion || null;
  const cierre = data.cierre || null;
  const shadow = data.shadow || null;
  const lecturas = data.lecturas_inline || [];
  const snap = data.snapshot_inline || {};
  const showJuicio = ESTADOS_VETADAS_O_RECHAZADAS.includes(data.estado);

  return (
    <>
      <Topbar
        title="Auditoría de candidata"
        subtitle={`${p.par || '?'} · ${p.estrategia || '?'} · ${STATE_LABEL[data.estado] || data.estado}`}
        right={
          <Link to="/logs" className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 border border-border hover:bg-muted transition-colors" data-testid="audit-back-logs">
            <ArrowLeft size={14} /> Volver a logs
          </Link>
        }
      />

      <div className="px-3 sm:px-4 lg:px-6 py-4 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        {/* Anchor sidebar */}
        <aside className="panel p-3 h-max sticky top-16 hidden lg:block" data-testid="audit-anchors">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Secciones</div>
          <ul className="space-y-1 text-sm">
            {SECTIONS.filter((s) => s.id !== 'juicio' || showJuicio).map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`anchor-${s.id}`}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors ${
                    activeAnchor === s.id
                      ? 'bg-muted text-foreground border-l-2 border-[hsl(var(--brand-accent))]'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60 border-l-2 border-transparent'
                  }`}
                >
                  <s.icon size={14} />
                  <span>{s.label}</span>
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
            <div>ID: <span className="font-num">{data.candidata_id}</span></div>
            <div>Creada: <span className="font-num">{iso(data.ts_creacion)}</span></div>
          </div>
        </aside>

        <div className="space-y-8">
          {/* 0. Diversidad de modelos por capa */}
          <section>
            <SectionHeader id="diversidad" icon={Cpu} title="0 · Diversidad de modelos por capa" subtitle="verificación rápida" />
            <ModelDiversityBoard lecturas={lecturas} propuesta={p} tribunal={t} comite={c} />
          </section>

          {/* 1. Snapshot origen */}
          <section>
            <SectionHeader id="origen" icon={Newspaper} title="1 · Snapshot origen" subtitle={`día ${snap.trading_day || '—'}`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              {(snap.pairs || []).map((pair) => <PairBlock key={pair.symbol} pair={pair} />)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="panel p-3">
                <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Newspaper size={12} /> Noticias del momento</div>
                {(snap.noticias || []).length === 0 && <div className="text-xs text-muted-foreground">Sin noticias registradas.</div>}
                <ul className="space-y-2 text-sm">
                  {(snap.noticias || []).map((n, i) => (
                    <li key={i} className="border-b border-border/60 pb-2 last:border-b-0">
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="uppercase">{n.impacto_estimado}</span>
                        <span>{n.fuente}</span>
                        {n.par_relacionado && <span className="font-num">· {n.par_relacionado}</span>}
                      </div>
                      <div className="font-medium">{n.titular}</div>
                      <div className="text-muted-foreground text-xs">{n.resumen}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="panel p-3">
                <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Clock size={12} /> Calendario / macro</div>
                {snap.macro && Object.keys(snap.macro).length > 0 ? (
                  <pre className="text-xs whitespace-pre-wrap font-num">{JSON.stringify(snap.macro, null, 2)}</pre>
                ) : <div className="text-xs text-muted-foreground">Sin eventos macro registrados.</div>}
                {snap.evento_spike && (
                  <div className="mt-2 alarm-warning border rounded-md p-2 text-xs">
                    <div className="font-medium mb-1">Evento spike detectado</div>
                    <pre className="whitespace-pre-wrap font-num">{JSON.stringify(snap.evento_spike, null, 2)}</pre>
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground mt-2">Ventana operativa: <span className="font-num">{JSON.stringify(snap.ventana_operativa || {})}</span></div>
              </div>
            </div>
          </section>

          {/* 2. Analistas A1-A5 */}
          <section>
            <SectionHeader id="analistas" icon={Users} title="2 · Lecturas de los 5 analistas (A1–A5)" subtitle="orden fijo, sin ocultar" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {lecturas.map((l) => <AnalystCard key={l.lectura_id || l.analista} lectura={l} />)}
              {lecturas.length === 0 && <div className="text-sm text-muted-foreground">Sin lecturas registradas.</div>}
            </div>
          </section>

          {/* 3. Candidata + sizing */}
          <section>
            <SectionHeader id="candidata" icon={Route} title="3 · Candidata del estratega + sizing desglosado" subtitle={p.estrategia} />
            <div className="panel p-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div className="panel-soft px-2 py-1.5"><div className="text-[11px] text-muted-foreground">Par</div><div className="font-num text-sm">{p.par}</div></div>
                <div className="panel-soft px-2 py-1.5"><div className="text-[11px] text-muted-foreground">Entrada</div><div className="font-num text-sm">{fmt(p.entry)}</div></div>
                <div className="panel-soft px-2 py-1.5"><div className="text-[11px] text-muted-foreground">Stop</div><div className="font-num text-sm">{fmt(p.stop)}</div></div>
                <div className="panel-soft px-2 py-1.5"><div className="text-[11px] text-muted-foreground">Objetivo</div><div className="font-num text-sm">{fmt(p.objetivo)}</div></div>
                <div className="panel-soft px-2 py-1.5"><div className="text-[11px] text-muted-foreground">R:R</div><div className="font-num text-sm">{fmt(sd?.rr, 2)}</div></div>
              </div>
              {p.tesis && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Tesis</div>
                  <p className="text-sm whitespace-pre-line">{p.tesis}</p>
                </div>
              )}
              {p.plan_salida && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Plan de salida</div>
                  <p className="text-sm whitespace-pre-line">{p.plan_salida}</p>
                </div>
              )}
              {(p['señales'] || p.señales || p.senales || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(p['señales'] || p.señales || p.senales || []).map((s, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-border bg-muted text-muted-foreground">{s}</span>
                  ))}
                </div>
              )}

              {sd && (
                <div className="mt-2 border-t border-border pt-3" data-testid="sizing-block">
                  <div className="text-xs text-muted-foreground mb-2">Sizing con cálculo visible</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    <KV k="Equity" v={<span className="font-num">{fmt(sd.equity_usdt)} USDT</span>} testId="sizing-equity" />
                    <KV k="Riesgo por trade" v={<span className="font-num">{fmt(sd.riesgo_usd)} USDT ({pct(sd.riesgo_pct, 3)})</span>} testId="sizing-riesgo" />
                    <KV k="Distancia stop" v={<span className="font-num">{fmt(sd.distancia_stop_abs, 4)} · {pct(sd.distancia_stop_pct, 3)}</span>} testId="sizing-dist-stop" />
                    <KV k="Distancia objetivo" v={<span className="font-num">{pct(sd.distancia_objetivo_pct, 3)}</span>} />
                    <KV k="Qty" v={<span className="font-num">{fmt(sd.qty, 6)}</span>} testId="sizing-qty" />
                    <KV k="Notional" v={<span className="font-num">{fmt(sd.notional_usdt)} USDT</span>} testId="sizing-notional" />
                    <KV k="Exposición" v={<span className="font-num">{pct(sd.exposicion_pct, 2)}</span>} testId="sizing-exposure" />
                    <KV k="R:R objetivo" v={<span className="font-num">{fmt(sd.r_multiplo_objetivo, 3)}</span>} />
                  </div>
                  {sd.notas?.length > 0 && (
                    <div className="mt-3 panel-soft p-3 font-num text-[11.5px] whitespace-pre-wrap" data-testid="sizing-notas">
                      {sd.notas.join('\n')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* 4. Tribunal */}
          <section>
            <SectionHeader
              id="tribunal"
              icon={Gavel}
              title="4 · Actas del tribunal F1–F3"
              subtitle={t
                ? `${t.votos_veto} veto${t.votos_veto === 1 ? '' : 's'} · oposición prom ${fmt(t.oposicion_promedio, 2)}${t.fiscal_disidente_principal ? ' · disidente ' + t.fiscal_disidente_principal : ''}`
                : ''}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(t?.actas || []).map((a) => <FiscalActa key={a.fiscal} acta={a} />)}
              {(!t || (t.actas || []).length === 0) && <div className="text-sm text-muted-foreground">Sin actas.</div>}
            </div>
          </section>

          {/* 5. Comité */}
          <section>
            <SectionHeader id="comite" icon={ScrollText} title="5 · Decisión del comité" subtitle={c?.veredicto} />
            {c ? (
              <div className="panel p-4 space-y-2">
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  <span className={`px-2 py-0.5 rounded border ${c.veredicto === 'presentar_a_jorge' ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : 'text-rose-400 border-rose-500/40 bg-rose-500/10'}`}>
                    {c.veredicto}
                  </span>
                  <span className="text-muted-foreground">Riesgo operacional: {c.riesgo_operacional}</span>
                  <span className="text-muted-foreground">Score: <span className="font-num">{fmt(c.score, 3)}</span></span>
                  {c.ranking && <span className="text-muted-foreground">Ranking del día: <span className="font-num">#{c.ranking}</span></span>}
                </div>
                {c.razonamiento && <p className="text-sm whitespace-pre-line">{c.razonamiento}</p>}
                {(c.fortalezas?.length > 0) && (
                  <div className="text-sm"><span className="text-muted-foreground text-xs">Fortalezas: </span>{c.fortalezas.join('; ')}</div>
                )}
                {(c.debilidades?.length > 0) && (
                  <div className="text-sm"><span className="text-muted-foreground text-xs">Debilidades: </span>{c.debilidades.join('; ')}</div>
                )}
                {(c.condiciones?.length > 0) && (
                  <div className="text-sm"><span className="text-muted-foreground text-xs">Condiciones: </span>{c.condiciones.join('; ')}</div>
                )}
              </div>
            ) : <div className="text-sm text-muted-foreground">Sin dictamen del comité.</div>}
          </section>

          {/* 6. Decisión Jorge */}
          <section>
            <SectionHeader id="jorge" icon={UserCheck} title="6 · Decisión de Jorge" subtitle={dj ? iso(dj.ts) : 'pendiente'} />
            {dj ? (
              <div className="panel p-4 text-sm">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  {dj.decision === 'aprobada' ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-400"><CheckCircle2 size={16} /> Aprobó</span>
                  ) : dj.decision === 'rechazada' ? (
                    <span className="inline-flex items-center gap-1.5 text-rose-400"><XCircle size={16} /> Rechazó</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Clock size={16} /> Expiró</span>
                  )}
                  <span className="text-xs text-muted-foreground font-num">{iso(dj.ts)}</span>
                </div>
                {dj.comentario && <p className="text-sm text-muted-foreground">“{dj.comentario}”</p>}
              </div>
            ) : (
              <div className="panel p-4 text-sm text-muted-foreground">
                Sin decisión directa registrada. Estado: {STATE_LABEL[data.estado] || data.estado}.
              </div>
            )}
          </section>

          {/* 7. Ejecución / cierre */}
          <section>
            <SectionHeader id="ejecucion" icon={Activity} title="7 · Ejecución simulada y cierre" subtitle={cierre ? `motivo ${cierre.motivo}` : 'no aplica'} />
            {ejec ? (
              <div className="panel p-4 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Entry fill</div><div className="font-num text-sm">{fmt(ejec.entry_fill)}</div></div>
                  <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Qty</div><div className="font-num text-sm">{fmt(ejec.qty, 6)}</div></div>
                  <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Stop inicial</div><div className="font-num text-sm">{fmt(ejec.stop_inicial)}</div></div>
                  <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Slippage entrada</div><div className="font-num text-sm">{pct(ejec.slippage_entry_pct, 3)}</div></div>
                </div>
                {(data.eventos_trailing?.length > 0) && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Evolución del trailing</div>
                    <ul className="text-xs space-y-1">
                      {data.eventos_trailing.map((ev, i) => (
                        <li key={i} className="panel-soft px-2 py-1 flex items-center justify-between gap-2">
                          <span className="font-num text-[11px]">{iso(ev.ts)}</span>
                          <span className="flex-1 px-2">{ev.tipo} · {ev.detalle}</span>
                          <span className="font-num">{ev.stop_nuevo ? fmt(ev.stop_nuevo) : ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {cierre && (
                  <div className="border-t border-border pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Salida</div><div className="font-num text-sm">{fmt(cierre.precio_salida)}</div></div>
                    <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">Motivo</div><div className="text-sm">{cierre.motivo}</div></div>
                    <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">R múltiplo</div><div className={`font-num text-sm ${cierre.r_multiplo > 0 ? 'pnl-pos' : cierre.r_multiplo < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(cierre.r_multiplo, 2)}</div></div>
                    <div className="panel-soft px-2 py-1.5"><div className="text-muted-foreground">PnL USDT</div><div className={`font-num text-sm ${cierre.pnl_usdt > 0 ? 'pnl-pos' : cierre.pnl_usdt < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(cierre.pnl_usdt, 2)}</div></div>
                  </div>
                )}
                {data.post_mortem?.resumen && (
                  <p className="text-sm text-muted-foreground italic">Post-mortem: {data.post_mortem.resumen}</p>
                )}
              </div>
            ) : (
              <div className="panel p-4 text-sm text-muted-foreground">La candidata no llegó a ejecución.</div>
            )}
          </section>

          {/* 8. Simulación en sombra (siempre visible cuando existe) */}
          <section>
            <SectionHeader
              id="sombra"
              icon={GitCompare}
              title="8 · Simulación en sombra"
              subtitle={shadow ? `resultado hipotético: ${shadow.resultado}` : 'no aplica'}
            />
            <ShadowSection shadow={shadow} />
          </section>

          {/* 9. Mi juicio (para vetadas/rechazadas) */}
          {showJuicio && (
            <section>
              <SectionHeader id="juicio" icon={Scale} title="9 · Mi juicio" subtitle="comparación Jorge ↔ sistema" />
              <div className="panel p-4">
                <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                  <Info size={14} />
                  <span>Esta candidata no llegó a ejecución real. Registra si hubieses operado igualmente para alimentar la métrica de coincidencia.</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <button
                    data-testid="juicio-si"
                    onClick={() => setJudgment((j) => ({ ...j, habria_operado: true }))}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${judgment.habria_operado ? 'border-[hsl(var(--brand-accent))] bg-[hsl(var(--brand-accent))]/10 text-[hsl(var(--brand-accent))]' : 'border-border bg-muted text-muted-foreground hover:text-foreground'}`}
                  >Sí, habría operado</button>
                  <button
                    data-testid="juicio-no"
                    onClick={() => setJudgment((j) => ({ ...j, habria_operado: false }))}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${!judgment.habria_operado ? 'border-rose-500/50 bg-rose-500/10 text-rose-300' : 'border-border bg-muted text-muted-foreground hover:text-foreground'}`}
                  >No, no habría operado</button>
                </div>
                <textarea
                  data-testid="juicio-nota"
                  className="w-full text-sm rounded-md border border-border bg-muted p-2 min-h-[80px]"
                  placeholder="(opcional) por qué sí o por qué no…"
                  value={judgment.nota}
                  onChange={(e) => setJudgment((j) => ({ ...j, nota: e.target.value }))}
                />
                <div className="flex items-center justify-between mt-3">
                  <div className="text-[11px] text-muted-foreground">
                    {data.juicio_jorge_auditoria
                      ? <>Último registro: <span className="font-num">{iso(data.juicio_jorge_auditoria.ts)}</span></>
                      : 'aún sin registro'}
                  </div>
                  <button
                    data-testid="juicio-guardar"
                    disabled={busy}
                    onClick={submitJudgment}
                    className="px-3 py-1.5 text-sm rounded-md text-white disabled:opacity-60"
                    style={{ background: 'hsl(var(--brand))' }}
                  >{busy ? 'Guardando…' : 'Guardar juicio'}</button>
                </div>
                {judgment.habria_operado && (
                  <div className="mt-3 text-xs alarm-warning border rounded-md p-2" data-testid="juicio-alerta">
                    <strong>Señal de auditoría:</strong> el sistema bloqueó pero tú sí habrías operado. Se contabiliza en “bloqueadas que Jorge habría operado”.
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Advertencias globales */}
          {(data.advertencias?.length > 0) && (
            <div className="panel alarm-warning border p-3 text-xs" data-testid="audit-advertencias">
              <div className="flex items-center gap-1 mb-1 font-medium"><AlertTriangle size={14} /> Advertencias</div>
              <ul className="list-disc pl-5">
                {data.advertencias.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
